import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdBlock from "@/components/AdBlock";
import api from "@/lib/api";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
}

const markdownComponents = {
  h2: (props) => (
    <h2 className="font-display text-xl md:text-2xl tracking-tight mt-10 mb-3" {...props} />
  ),
  h3: (props) => (
    <h3 className="font-display text-lg md:text-xl tracking-tight mt-8 mb-2" {...props} />
  ),
  p: (props) => (
    <p className="text-base md:text-lg text-white/80 leading-relaxed mt-3" {...props} />
  ),
  a: (props) => (
    <a className="text-[#00E5FF] hover:text-white underline underline-offset-4" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  strong: (props) => <strong className="text-white font-semibold" {...props} />,
  ul: (props) => <ul className="mt-3 space-y-1.5 list-disc pl-5 text-white/80" {...props} />,
  ol: (props) => <ol className="mt-3 space-y-1.5 list-decimal pl-5 text-white/80" {...props} />,
  code: ({ inline, ...props }) =>
    inline ? (
      <code className="px-1.5 py-0.5 rounded bg-white/10 text-[#00E5FF] text-sm font-mono" {...props} />
    ) : (
      <code className="block font-mono text-sm" {...props} />
    ),
  pre: (props) => (
    <pre className="mt-3 rounded-xl border border-white/10 bg-black/40 p-4 overflow-x-auto" {...props} />
  ),
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/blog/${slug}`)
      .then(({ data }) => {
        if (!mounted) return;
        setPost(data);
        document.title = `${data.title} — Giprompthub Blog`;
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
          m = document.createElement("meta");
          m.setAttribute("name", "description");
          document.head.appendChild(m);
        }
        m.setAttribute("content", data.excerpt);
      })
      .catch(() => setNotFound(true))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <main className="max-w-[820px] mx-auto px-5 md:px-10 py-8 md:py-12">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors font-mono-label"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to blog
        </Link>

        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        )}

        {notFound && !loading && (
          <div className="py-24 text-center" data-testid="blog-post-not-found">
            <div className="font-display text-3xl mb-2">Article not found</div>
            <p className="text-white/50">
              This guide may have been moved. Browse all guides instead.
            </p>
          </div>
        )}

        {post && (
          <article data-testid={`blog-post-${post.slug}`} className="mt-6">
            <span className="font-mono-label text-[#00E5FF]">{post.category}</span>
            <h1
              data-testid="blog-post-title"
              className="mt-3 font-display text-3xl md:text-5xl tracking-tight leading-[1.05]"
            >
              {post.title}
            </h1>
            <p className="mt-5 text-white/60 text-lg leading-relaxed">
              {post.excerpt}
            </p>
            <div className="mt-4 flex items-center gap-3 text-xs text-white/40 font-mono">
              <span data-testid="blog-post-author">By {post.author}</span>
              <span>·</span>
              <span data-testid="blog-post-views">{post.views} views</span>
            </div>

            {post.cover_image_url && (
              <div className="mt-8 rounded-2xl overflow-hidden border border-white/10 bg-[#0E0E12] aspect-[16/9]">
                <img
                  src={resolveUrl(post.cover_image_url)}
                  alt={post.title}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="mt-6" data-testid="blog-post-content">
              <ReactMarkdown components={markdownComponents}>
                {post.content}
              </ReactMarkdown>
            </div>

            <div className="mt-6">
              <AdBlock variant="inline" testId="ad-blog-inline" />
            </div>

            <div className="mt-12 pt-8 border-t border-white/5 flex items-center justify-between flex-wrap gap-4">
              <Link
                to="/"
                data-testid="blog-post-cta-gallery"
                className="inline-flex items-center gap-2 rounded-full bg-[#E0007A] hover:bg-[#ff1493] text-white font-semibold text-sm px-6 py-3 glow-magenta transition-transform hover:scale-[1.02]"
              >
                Browse the prompt gallery
              </Link>
              <Link
                to="/blog"
                data-testid="blog-post-cta-more"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-[#00E5FF]/60 text-white/80 hover:text-[#00E5FF] text-sm px-6 py-3 transition-colors"
              >
                More guides
              </Link>
            </div>
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
}
